import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../i18n/LanguageContext';

import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../api/config";

import { showSuccess, showError, showInfo, redirectPage } from "../utils/notification";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [logoutReason, setLogoutReason] = useState('');
  const { isAuthenticated, refresh } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    // Pen-test fix 5.4 — if we were bounced here because the account was
    // signed in from another device, show the reason so the user knows.
    try {
      const reason = sessionStorage.getItem('logout_reason');
      if (reason) {
        setLogoutReason(reason);
        sessionStorage.removeItem('logout_reason');
      }
    } catch {}
  }, [navigate, isAuthenticated]);

  const mapUserTypeToRole = (userType) => {
    if (!userType) return '';
    const t = String(userType).toUpperCase();
    if (t === 'ADMIN') return 'admin';
    if (t === 'DEPT_ADMIN') return 'dept_admin';
    if (t === 'DEPARTMENT') return 'department';
    if (t === 'STATE') return 'state';
    if (t === 'NGO' || t === 'OTHERS') return 'ngo';
    return t.toLowerCase();
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      showError('Please fill all fields');
      return;
    }

    try {
      const payload = {
        email_id: email,
        password: password,
      };

      // Pen-test remediation — login now sets the JWT as an HTTP-only cookie
      // on the server; the browser stores it automatically. We must send
      // `withCredentials` so the cookie is accepted / echoed back.
      const response = await axios.post(
        `${API_BASE_URL}/auth/login`,
        payload,
        { withCredentials: true }
      );

      if (response.data && response.data.success === true) {
        // Pen-test remediation — do not write auth credentials or profile
        // metadata to localStorage. The JWT already lives in the HttpOnly
        // cookie the server set. We ask the AuthContext to (re-)hydrate
        // from /auth/me, then navigate. All UI components read the user
        // through useAuth() instead of localStorage.
        await refresh();
        showSuccess(response.data.message || 'Login successful');
        // Route by role: STATE -> state dashboard, DEPT_ADMIN -> user master,
        // everyone else -> normal dashboard.
        const nextRole = mapUserTypeToRole(response.data.data?.user?.user_type);
        let next = '/dashboard';
        if (nextRole === 'state') next = '/state-dashboard';
        else if (nextRole === 'dept_admin') next = '/dept-admin-dashboard';
        window.location.href = next;
      } else {
        showError(response.data?.message || 'Login failed');
      }
    } catch (error) {
      showError(error?.response?.data?.message || 'Login failed');
    }
  };

  const handleRegister = () => {
    window.location.href = '/register';
  };

  const { lang, toggleLang, tr } = useLang();
  const inputCls = 'w-full px-3 py-2.5 text-sm text-white placeholder-white/60 bg-white/10 border border-white/40 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent';

  return (
    <div className="relative min-h-screen flex flex-col bg-brand-900 overflow-hidden">
      {/* Background video of a lake (self-hosted: the server CSP blocks
          third-party media). Falls back to the poster image if the video
          is missing or the browser can't autoplay it. */}
      <video
        className="absolute inset-0 w-full h-full object-cover motion-reduce:hidden"
        src="/videos/lake.mp4"
        poster="/images/water1.jpg"
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-900/50 via-brand-900/20 to-brand-900/40" aria-hidden="true" />
      <div className="relative min-h-screen flex flex-col">
        {/* Header — same style as the site navbar */}
        <header className="w-full bg-brand-900/40 backdrop-blur-sm border-b border-white/15">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 sm:gap-4">
              <img src="/images/cm.png" alt="Hon'ble Chief Minister of Tamil Nadu" className="h-12 w-12 sm:h-[72px] sm:w-[72px] rounded-full object-cover object-top border-2 border-white/70" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              <div className="leading-tight">
                <h1 className="text-lg lg:text-xl font-semibold text-white">ஏரி இணையதளம்</h1>
                <p className="text-[0.9rem] text-brand-100 tracking-wide">தமிழ்நாடு அரசு</p>
              </div>
            </a>
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={toggleLang}
                className="text-sm font-medium text-brand-100 hover:text-white border border-white/20 px-3 py-1.5 hover:bg-white/10"
              >
                {lang === 'en' ? 'தமிழ்' : 'English'}
              </button>
              <img src="/images/tnlogo.png" alt="Government of Tamil Nadu" className="h-12 w-12 sm:h-[72px] sm:w-[72px] object-contain rounded-full bg-white border-2 border-white/70 p-1" />
            </div>
          </div>
        </header>

        {/* Main — form sits directly on the video */}
        <main className="flex-1 flex items-center px-4 py-10">
          <div className="w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
            {/* Portal message (desktop) */}
            <div className="hidden lg:block text-white">
              <h2 className="text-5xl font-semibold drop-shadow">ஏரி இணையதளம்</h2>
              <div className="w-16 h-1 bg-brand-accent my-5" />
              <p className="text-lg leading-relaxed text-white/90 max-w-md drop-shadow">
                {tr(
                  'Restoration of water bodies with the participation of volunteers and issuance of No Objection Certificates (NOC).',
                  'தன்னார்வலர்களின் பங்களிப்புடன் நீர்நிலைகளைப் புனரமைத்தல் மற்றும் தடையின்மைச் சான்று (NOC) வழங்குதல்.'
                )}
              </p>
              <p className="mt-4 text-sm font-semibold text-brand-accent drop-shadow">
                {tr('One Platform • Easy Application • Faster Approvals • Transparent Monitoring', 'ஒரே தளம் • எளிய விண்ணப்பம் • விரைவான அனுமதி • வெளிப்படையான கண்காணிப்பு')}
              </p>
            </div>

            {/* Login — frosted glass over the video */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto bg-white/10 backdrop-blur-md border border-white/25 border-t-4 border-t-brand-accent p-7 sm:p-9 text-white shadow-2xl">
              <h2 className="text-2xl font-semibold mb-1">{tr('Login', 'உள்நுழைவு')}</h2>
              <p className="text-sm text-white/80 mb-6">
                {tr('Sign in to apply for and track NOC applications.', 'தடையின்மைச் சான்று விண்ணப்பிக்கவும் கண்காணிக்கவும் உள்நுழையவும்.')}
              </p>

              {logoutReason && (
                <div className="mb-4 p-3 text-sm text-amber-100 bg-amber-500/20 border-l-4 border-amber-400">
                  {logoutReason}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4 login-glass">
                <div>
                  <label className="block text-sm font-medium mb-1 text-white/90">{tr('Email ID / Username', 'மின்னஞ்சல் / பயனர் பெயர்')}</label>
                  <input
                    type="text"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                    placeholder={tr('Enter your email or username', 'உங்கள் மின்னஞ்சல் அல்லது பயனர் பெயரை உள்ளிடவும்')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-white/90">{tr('Password', 'கடவுச்சொல்')}</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputCls}
                    placeholder={tr('Enter your password', 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்')}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-brand-accent hover:bg-teal-400 text-brand-900 py-2.5 px-4 font-semibold text-sm transition-colors focus:outline-none focus:ring-4 focus:ring-white/40"
                >
                  {tr('Login', 'உள்நுழைய')}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-white/20 text-center">
                <p className="text-white/80 text-sm mb-2">{tr('New Organization / Applicant?', 'புதிய அமைப்பு / விண்ணப்பதாரரா?')}</p>
                <button
                  type="button"
                  onClick={handleRegister}
                  className="px-5 py-2 text-sm font-semibold border border-white/60 text-white hover:bg-white/10"
                >
                  {tr('Register Now for NOC', 'தடையின்மைச் சான்றுக்கு இப்போதே பதிவு செய்க')}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
